/* global CustomEvent */
import React, { useState, useEffect } from 'react';
import { useQuery, requestPack } from 'skybolt-api';
import {
  TalentGrid,
  Modal,
  Icon,
  useRouter,
  InputText,
  Form,
  Button,
  Text,
  Rule,
  Theme,
  TalentSearch,
  Card,
  hexToRGBA,
} from 'skybolt-ui';
import Talent from './Talent';


export default () => {


  // Load Data

  const {match:{params:{ agencyId, division, tag }}} = useRouter();
  const [search, setSearch] = useState({});
  const [limit, setLimit] = useState(100);


  const data = useQuery(`{
    agency(agencyId:$agencyId) {
      agencyId
      name
      talents: publicTalents(search:$search, division:$division, tag:$tag, limit:$limit) {
        talentId
        image(width:130, height:130, face:true)
        name: stageName
        info
      }
      primaryColor
      accentColor
      talentFields
      embedBackgroundColor
    }
  }`, {
    agencyId,
    search,
    division,
    tag,
    limit,
  });



  // Control Talent Modal
  // Opened when talent is clicked.

  const [talentId, setTalentId] = useState(null);
  const [talentTab, setTalentTab] = useState(null);
  const [talentOpen, setTalentOpen] = useState(false);
  const [talentTop, setTalentTop] = useState(24);

  function handleClick(tab, talentId, e) {
    try {
      setTalentTop(e.target.getBoundingClientRect().top - 40);
    }
    catch(error) {
      setTalentTop(24);
    }
    setTalentId(talentId);
    setTalentTab(tab);
    setTalentOpen(true);
  }



  // Toggle talent search.

  const [isSearching, setSearching] = useState(false);
  const toggleSearching = () => setSearching(!isSearching);



  // Pull talents either from the spotlight, or from the whole agency.

  let talents = data ? data.agency.talents : [];



  // Talent selections are held in state. Instead of pulling a `selected` field
  // on each talent, we map all talents and inject it with a value from state.

  const [selected, setSelected] = useState({});
  const handleSelect = (sel, id) => {
    const newValue = {...selected};
    if(!sel) {
      delete newValue[id];
    }
    else {
      newValue[id] = true;
    }
    setSelected(newValue);
  };

  talents = talents.map(t => ({...t, selected:!!selected[t.talentId]}));



  // Handle a casting request.

  const [requestOpen, setRequestOpen] = useState(false);
  const [request, setRequest] = useState({});
  const [isRequesting, setRequesting] = useState(false);
  const [error, setError] = useState("");

  const createCastingRequest = async () => {
    if(isRequesting) {
      return;
    }
    setRequesting(true);
    try {
      await requestPack({
        ...request,
        agencyIds: [agencyId],
        talents: Object.keys(selected),
      });
    }
    catch(error) {
      console.log(error);
      setError(error.message || "Something went wrong. Please try again.");
      setRequesting(false);
      return;
    }
    setError(null);
    setRequestOpen('sent');
    setSelected([]);
    setRequesting(false);
  };



  // Only use public fields.

  const fields = data ? {...data.agency.talentFields} : {};
  for(let id in fields) {
    if(fields[id].isPublic !== true || fields[id].isEnabled === false) {
      delete fields[id];
    }
  }



  // Broadcast size changes to embedding script.

  const [height, setHeight] = useState({});
  useEffect(() => {
    window.parent.postMessage({height}, "*");
  }, [height]);

  useEffect(() => {
    // todo: implement resize observer for modern browsers
    // https://developers.google.com/web/updates/2016/10/resizeobserver
    // Fall back on resize observer.
    const timer = setInterval(() => {
      setHeight(document.body.scrollHeight);
    }, 1000);
    return () => clearInterval(timer);
  }, [data]);



  // Layout

  if(!data) {
    return (
      <Icon name="loading" style={{margin:"160px auto 0"}}/>
    );
  }

  const backgroundColor = data.agency.embedBackgroundColor || "#fafafa";

  return (
    <Theme
      theme={{
        accentColor: data.agency.accentColor,
        primaryColor: data.agency.primaryColor,
      }}>
      <div style={{width:'100%', backgroundColor, padding:8}}>
        <div style={{display:'flex', alignItems:'flex-end', height:40}}>
          <Text title>TALENT</Text>
          <div style={{flex:1}}/>
          {Object.keys(selected).length > 0 &&
            <Button
              accent
              label="Create Casting Request"
              onClick={() => setRequestOpen(true)}
            />
          }
          <Button title onClick={toggleSearching}>
            <Icon color="white" name={isSearching ? "close" : "tune"}/>
          </Button>
        </div>
        <Rule/>

        {isSearching &&
          <Card style={{padding:16, marginBottom:32}}>
            <TalentSearch
              fields={fields}
              value={search}
              onChange={value => setSearch(value)}
              useLocation={false}
              includePrivateMedia={false}
            />
          </Card>
        }

        <div style={{height:24}}/>

        <TalentGrid
          talents={talents}
          small
          onClick={handleClick}
          onSelect={handleSelect}
        />

        {talents.length === limit &&
          <div style={{textAlign:'center'}}>
            <Button accent onClick={() => setLimit(limit + 40)} label="Load More" style={{margin:'24px 0'}}/>
          </div>
        }
      </div>


      {/* Talent Modal. */}

      <Modal
        status={talentOpen}
        onRequestClose={() => setTalentOpen(false)}
        overlayStyle={{
          display: 'block',
          backgroundColor: hexToRGBA(backgroundColor, 0.8),
          paddingTop: talentTop,
        }}>
        <Talent talentId={talentId} tab={talentTab}/>
      </Modal>


      {/* Casting Request Modal. */}

      <Modal
        status={requestOpen}
        onRequestClose={() => setRequestOpen(false)}
        overlayStyle={{
          display:'block',
          backgroundColor: hexToRGBA(backgroundColor, 0.4),
        }}>
        <Form value={request} onChange={val => setRequest(val)} style={{padding:16}}>

          <Text large style={{marginBottom:24}}>Create Casting Request</Text>

          <InputText name="from.name" title="Your Name"/>
          <InputText name="from.email" title="Email Address"/>
          <InputText name="name" title="Package Name"/>
          <InputText name="message" title="Message"/>

          {error && <Text error>{error}</Text>}

          <div style={{display:'flex', justifyContent:'flex-end'}}>
            <Button disabled={isRequesting} raised accent onClick={createCastingRequest}>
              {isRequesting ? "Hang Tight..." : "Send"}
            </Button>
          </div>
        </Form>
      </Modal>
    </Theme>
  );

};